import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, StatusBadge, BackButton, MobileSearchField, Switch, DropdownMenu } from '../components/common';
import { navigateBack } from '../utils/navigationUtils';
import { useLocation } from 'react-router-dom';
import { useClubPermissions } from '../hooks/useClubPermissions';
import { APP_PAGE_BLEED_ROOT, APP_PAGE_GUTTER } from '../utils/sessionMobileLayout';
import ConfirmationModal from '../components/common/ConfirmationModal';
import CourtsList from '../components/courts/CourtsList';
import UpcomingMatch from '../components/courts/UpcomingMatch';
import WaitingList from '../components/players/WaitingList';
import PlayerStats from '../components/players/PlayerStats';
import AddPlayerForm from '../components/players/AddPlayerForm';
import usePlayerManagement from '../hooks/usePlayerManagement';
import useCourtManagement from '../hooks/useCourtManagement';
import { supabase } from '../utils/supabase';
import { updateSessionStatus } from '../services/sessionService';
import { useSeason, SeasonProvider } from '../context/SeasonContext';
import CurrentPartnerships from '../components/players/CurrentPartnerships';
import GuestManagement from '../components/guests/GuestManagement';
import { ClubLeagueSquadSection } from '../components/clubs/dashboard/ClubLeagueSquadPanel';
import { updatePartnerships } from '../services/partnershipService';
import SessionTemplateForm from '../components/sessions/SessionTemplateForm';
import RejectJoinRequestModal from '../components/clubs/RejectJoinRequestModal';
import AccountLinkRequestCard from '../components/clubs/AccountLinkRequestCard';
import { getPendingAccountLinkRequestsForAdmin } from '../services/guestAccountLinkingService';
import { Heading, Text, Typography } from '../components/common/Typography';
import { Card, FeatureCard, StatsCard } from '../components/common/Card';
import { FormSection, FormField, Input, NumberInput } from '../components/common/Form';
import { initializePlayerEloRatings } from '../services/guestService';
import { getDefaultStartingElo } from '../utils/constants';
import Pagination from '../components/common/Pagination';
import { getPlayerFullName, playerMatchesSearchQuery } from '../utils/playerUtils';
import PasteAliasManager from '../components/clubs/PasteAliasManager';
import PlayerAvatar from '../components/common/PlayerAvatar';
import ClubMemberNotesModal from '../components/clubs/ClubMemberNotesModal';
import {
  approveClubReactivation,
  declineClubReactivation,
  getClubReactivationRequests,
  setClubMemberActive,
  setClubMemberInactive,
  setClubMemberTrial,
} from '../services/clubMembershipService';

const countClubAdmins = (members) =>
  (members || []).filter((member) => member.club_role === 'admin').length;

const wouldRemoveLastClubAdmin = (members, playerId, newRole) => {
  if (newRole === 'admin') return false;
  const member = (members || []).find((m) => m.player_id === playerId);
  if (member?.club_role !== 'admin') return false;
  return countClubAdmins(members) <= 1;
};

// Analytics Card Component
const ANALYTICS_ICON_BG = {
  blue:   'bg-info-light',
  green:  'bg-success-light',
  purple: 'bg-info-light',
  orange: 'bg-warning-light',
  red:    'bg-danger-light',
  teal:   'bg-success-light',
};

const AnalyticsCard = ({ title, value, subtitle, icon, color = 'blue' }) => (
  <StatsCard
    icon={icon}
    title={title}
    value={value}
    subtitle={subtitle}
    iconBg={ANALYTICS_ICON_BG[color] || 'bg-surface-subtle'}
  />
);

// Progress Bar Component
const ProgressBar = ({ percentage, label, color = 'primary' }) => (
  <div className="mb-4">
    <div className="flex justify-between text-sm text-text-secondary mb-1">
      <span>{label}</span>
      <span>{percentage}%</span>
    </div>
    <div className="w-full bg-border-medium rounded-full h-2">
      <div 
        className={`bg-${color} h-2 rounded-full transition-all duration-300`}
        style={{ width: `${percentage}%` }}
      ></div>
    </div>
  </div>
);

// Dashboard Content Component
const DashboardContent = ({ analytics, season, seasonName, setSeasonName, loading, error, success, handleStartNewSeason, setShowSeasonConfirmModal }) => (
  <div>
    {/* Key Metrics Grid */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      <AnalyticsCard
        title="Total Players"
        value={analytics.totalPlayers}
        subtitle={`${analytics.activePlayers} active`}
        icon="👥"
        color="blue"
      />
      <AnalyticsCard
        title="Total Sessions"
        value={analytics.totalSessions}
        subtitle={`${analytics.sessionsThisMonth} this month`}
        icon="🏸"
        color="green"
      />
      <AnalyticsCard
        title="Avg Session Size"
        value={analytics.averageSessionSize}
        subtitle="players per session"
        icon="📊"
        color="purple"
      />
      <AnalyticsCard
        title="Active Players"
        value={`${analytics.activePlayers30DaysPercentage}%`}
        subtitle={`${analytics.activePlayers30Days} of ${analytics.totalPlayers} active`}
        icon="📈"
        color="orange"
      />
    </div>

    {/* Detailed Analytics */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
      {/* Gender Demographics */}
      <div className="bg-surface rounded-lg shadow-md p-6 border border-border-medium">
        <h3 className="text-lg font-semibold text-text-primary mb-4">Gender Demographics</h3>
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-sm text-text-secondary">Male</span>
            <span className="font-semibold text-text-primary">{analytics.genderSplit.male}</span>
          </div>
          <ProgressBar 
            percentage={analytics.totalPlayers > 0 ? Math.round((analytics.genderSplit.male / analytics.totalPlayers) * 100) : 0} 
            label="Male" 
            color="primary"
          />
          
          <div className="flex justify-between items-center">
            <span className="text-sm text-text-secondary">Female</span>
            <span className="font-semibold text-text-primary">{analytics.genderSplit.female}</span>
          </div>
          <ProgressBar 
            percentage={analytics.totalPlayers > 0 ? Math.round((analytics.genderSplit.female / analytics.totalPlayers) * 100) : 0} 
            label="Female" 
            color="female"
          />
          
          <div className="flex justify-between items-center">
            <span className="text-sm text-text-secondary">Other</span>
            <span className="font-semibold text-text-primary">{analytics.genderSplit.other}</span>
          </div>
          <ProgressBar 
            percentage={analytics.totalPlayers > 0 ? Math.round((analytics.genderSplit.other / analytics.totalPlayers) * 100) : 0} 
            label="Other" 
            color="accent"
          />
        </div>
      </div>

      {/* ELO Averages */}
      <div className="bg-surface rounded-lg shadow-md p-6 border border-border-medium">
        <h3 className="text-lg font-semibold text-text-primary mb-4">Average ELO Ratings</h3>
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-sm text-text-secondary">Mixed Doubles</span>
            <span className="font-semibold text-primary">{analytics.averageElo.mixed}</span>
          </div>
          <div className="w-full bg-border-medium rounded-full h-2">
            <div 
              className="bg-primary h-2 rounded-full"
              style={{ width: `${Math.min((analytics.averageElo.mixed / 2000) * 100, 100)}%` }}
            />
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-sm text-text-secondary">Levels</span>
            <span className="font-semibold text-success">{analytics.averageElo.levels}</span>
          </div>
          <div className="w-full bg-border-medium rounded-full h-2">
            <div 
              className="bg-success h-2 rounded-full"
              style={{ width: `${Math.min((analytics.averageElo.levels / 2000) * 100, 100)}%` }}
            />
          </div>
        </div>
      </div>
    </div>

    {/* Recent Sessions */}
    <div className="bg-surface rounded-lg shadow-md p-6 mb-8 border border-border-medium">
      <h3 className="text-lg font-semibold text-text-primary mb-4">Recent Sessions</h3>
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr className="border-b border-border-medium">
              <th className="text-left py-2 text-text-secondary">Date</th>
              <th className="text-left py-2 text-text-secondary">Status</th>
              <th className="text-left py-2 text-text-secondary">Players</th>
            </tr>
          </thead>
          <tbody>
            {analytics.recentSessions.map((session) => (
              <tr key={session.id} className="border-b border-border-medium">
                <td className="py-2 text-text-primary">{new Date(session.session_date).toLocaleDateString()}</td>
                <td className="py-2">
                  <span className={`px-2 py-1 rounded text-xs ${
                    session.session_status === 'completed' ? 'bg-success-light text-success-dark' :
                    session.session_status === 'ongoing' ? 'bg-info-light text-info-dark' :
                    'bg-surface-subtle text-text-secondary'
                  }`}>
                    {session.session_status}
                  </span>
                </td>
                <td className="py-2 text-text-primary">{analytics.averageSessionSize}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>

    {/* Season Management */}
    <div className="bg-surface rounded-lg shadow-md p-6 border border-border-medium">
      <h3 className="text-lg font-semibold mb-4">Season Management</h3>
      <FormField>
        <Input
          label="Enter New Season Name"
          type="text"
          placeholder={`Season ${(season?.season_number || 0) + 1}`}
          value={seasonName}
          onChange={e => setSeasonName(e.target.value)}
          disabled={loading}
        />
      </FormField>
      <Button
        variant="primary"
        onClick={() => setShowSeasonConfirmModal(true)}
        disabled={loading}
      >
        {loading ? 'Starting New Season...' : 'Start New Season'}
      </Button>
      {error && <div className="text-danger mt-2">{error}</div>}
      {success && <div className="text-green-600 mt-2">{success}</div>}
    </div>
  </div>
);


const joinRequestAvatarPlayer = (request) => ({
  name: request?.player_name || 'Player',
  gender: request?.player_gender,
  avatar_url: request?.player_avatar_url,
});

const MemberStatusBadges = ({ member }) => {
  const isInactive = member.membership_status === 'inactive';
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <StatusBadge variant={isInactive ? 'danger' : 'active'} size="small">
        {isInactive ? 'Inactive' : 'Active'}
      </StatusBadge>
      {member.reactivation_requested_at ? (
        <StatusBadge variant="pending" size="small">Reactivation requested</StatusBadge>
      ) : null}
    </div>
  );
};

const ClubMemberRow = ({
  member,
  loading,
  updatingRoleFor,
  membershipActionFor,
  onRoleChange,
  onTrialToggle,
  onNotes,
  onRestore,
  onSetInactive,
}) => {
  const player = member.players || {};
  const isOwner = member.club_role === 'owner';
  const isInactive = member.membership_status === 'inactive';
  const displayName = player.first_name || player.last_name
    ? `${player.first_name || ''} ${player.last_name || ''}`.trim()
    : 'Unknown Player';
  const actionBusy = membershipActionFor === member.player_id;
  const roleBusy = updatingRoleFor === member.player_id;
  const roleOptions = [
    { value: 'member', label: 'Member' },
    { value: 'host', label: 'Host' },
    { value: 'admin', label: 'Admin' },
  ];

  const menuItems = [{ label: 'Notes', onClick: () => onNotes(member) }];
  if (!isOwner && isInactive) {
    menuItems.push({
      label: 'Make active',
      variant: 'success',
      disabled: actionBusy,
      onClick: () => onRestore(member),
    });
  }
  if (!isOwner && !isInactive) {
    menuItems.push({
      label: 'Set inactive',
      variant: 'danger',
      disabled: actionBusy,
      onClick: () => onSetInactive(member),
    });
  }

  return (
    <tr className={isInactive ? 'bg-surface-subtle' : ''}>
      <td className="px-4 py-2.5">
        <div className="flex min-w-[16rem] items-center gap-3">
          <PlayerAvatar player={player} size="md" />
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <p className="truncate text-sm font-medium text-text-primary">{displayName}</p>
              <MemberStatusBadges member={member} />
            </div>
            <p className="truncate text-xs text-text-secondary">{player.email || 'No email'}</p>
          </div>
        </div>
      </td>
      <td className="px-4 py-2.5">
        {isOwner ? (
          <span className="inline-flex items-center rounded-full bg-primary/15 px-2.5 py-0.5 text-xs font-semibold text-primary">
            Owner
          </span>
        ) : (
          <select
            value={member.club_role || 'member'}
            onChange={(e) => onRoleChange(member.player_id, e.target.value)}
            disabled={roleBusy || loading || isInactive}
            className="form-input block w-32 rounded-md border border-border bg-surface px-2 py-1 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-primary"
            aria-label={`Role for ${displayName}`}
          >
            {roleOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        )}
      </td>
      <td className="px-4 py-2.5">
        <Switch
          checked={Boolean(member.is_trial)}
          disabled={actionBusy || isOwner}
          onChange={(e) => onTrialToggle(member, e.target.checked)}
          aria-label={`On trial for ${displayName}`}
        />
      </td>
      <td className="px-4 py-2.5 text-right">
        <DropdownMenu
          align="right"
          usePortal
          useMobileSheet
          sheetTitle={`Actions for ${displayName}`}
          trigger={
            <Button
              variant="outline"
              size="small"
              className="inline-flex items-center gap-1"
              aria-label={`Actions for ${displayName}`}
              aria-haspopup="menu"
            >
              Actions
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </Button>
          }
          items={menuItems}
        />
      </td>
    </tr>
  );
};

// Player Management Content Component
const PlayerManagementContent = ({
  adminClub,
  joinRequests,
  joinRequestsLoading,
  handleJoinRequest,
  handleRejectClick,
  loading,
  currentUser,
  onJoinRequestsChanged,
}) => {
  const pendingRequests = joinRequests.filter(r => r.status === 'pending');
  const [historyFilter, setHistoryFilter] = useState('pending'); // 'pending', 'all', 'approved', 'rejected', 'withdrawn_by_user'
  const [accountLinkRequests, setAccountLinkRequests] = useState([]);
  const [accountLinkRequestsLoading, setAccountLinkRequestsLoading] = useState(false);
  const [reactivationRequests, setReactivationRequests] = useState([]);
  const [reactivationLoading, setReactivationLoading] = useState(false);

  const loadAccountLinkRequests = useCallback(async () => {
    if (!adminClub?.id || !currentUser?.id) {
      setAccountLinkRequests([]);
      return;
    }

    setAccountLinkRequestsLoading(true);
    try {
      const requests = await getPendingAccountLinkRequestsForAdmin(adminClub.id, currentUser.id);
      setAccountLinkRequests(requests);
    } catch (err) {
      console.error('[ClubAdmin] Failed to load account link requests:', err);
    } finally {
      setAccountLinkRequestsLoading(false);
    }
  }, [adminClub?.id, currentUser?.id]);

  useEffect(() => {
    loadAccountLinkRequests();
  }, [loadAccountLinkRequests]);

  const handleAccountLinkResolved = (requestUserId) => {
    setAccountLinkRequests((prev) => prev.filter((r) => r.user_id !== requestUserId));
    onJoinRequestsChanged?.();
  };

  const requestsLoading = joinRequestsLoading || accountLinkRequestsLoading || reactivationLoading;
  const hasPendingAccountLinks = accountLinkRequests.length > 0;
  const hasPendingJoinRequests = pendingRequests.length > 0;
  const hasAnyRequests = joinRequests.length > 0 || hasPendingAccountLinks || reactivationRequests.length > 0;
  const showEmptyState = !requestsLoading && !hasAnyRequests;
  
  // Filter requests based on selected filter
  const getFilteredRequests = () => {
    if (historyFilter === 'pending') {
      return joinRequests.filter(r => r.status === 'pending');
    }
    
    if (historyFilter === 'all') {
      return joinRequests.filter(r => r.status !== 'pending');
    }
    
    return joinRequests.filter(r => r.status === historyFilter);
  };
  
  const filteredRequests = getFilteredRequests();

  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState(null);
  const [updatingRoleFor, setUpdatingRoleFor] = useState(null);
  const [memberSearch, setMemberSearch] = useState('');
  const [membersCurrentPage, setMembersCurrentPage] = useState(1);
  const [membersItemsPerPage, setMembersItemsPerPage] = useState(10);
  const [memberStatusFilter, setMemberStatusFilter] = useState('active');
  const [memberRoleFilter, setMemberRoleFilter] = useState('all');
  const [notesMember, setNotesMember] = useState(null);
  const [inactiveConfirmMember, setInactiveConfirmMember] = useState(null);
  const [membershipActionFor, setMembershipActionFor] = useState(null);

  const loadReactivationRequests = useCallback(async () => {
    if (!adminClub?.id) {
      setReactivationRequests([]);
      return;
    }
    setReactivationLoading(true);
    try {
      const rows = await getClubReactivationRequests(adminClub.id);
      setReactivationRequests(rows);
    } catch (err) {
      console.error('[ClubAdmin] Failed to load reactivation requests:', err);
    } finally {
      setReactivationLoading(false);
    }
  }, [adminClub?.id]);

  const fetchMembers = useCallback(async () => {
    if (!adminClub?.id) return;

    setMembersLoading(true);
    setMembersError(null);

    try {
      const { data, error } = await supabase
        .from('players_club')
        .select(`
          player_id,
          club_role,
          is_active,
          membership_status,
          is_trial,
          reactivation_requested_at,
          players:player_id (
            id,
            first_name,
            last_name,
            email,
            gender,
            avatar_url
          )
        `)
        .eq('club_id', adminClub.id)
        .neq('club_role', 'guest')
        .order('club_role', { ascending: true });

      if (error) throw error;

      setMembers(data || []);
    } catch (err) {
      console.error('[ClubAdmin] Failed to fetch club members:', err);
      setMembersError('Failed to load club members');
    } finally {
      setMembersLoading(false);
    }
  }, [adminClub?.id]);

  const filteredMembers = useMemo(() => {
    return members
      .filter((member) => {
        if (memberStatusFilter === 'active') {
          return member.membership_status !== 'inactive';
        }
        if (memberStatusFilter === 'trial') {
          return Boolean(member.is_trial);
        }
        if (memberStatusFilter === 'inactive') {
          return member.membership_status === 'inactive';
        }
        return true;
      })
      .filter((member) => {
        if (memberRoleFilter === 'all') return true;
        return member.club_role === memberRoleFilter;
      })
      .filter((member) => playerMatchesSearchQuery(member.players, memberSearch))
      .sort((a, b) =>
        getPlayerFullName(a.players || {}).localeCompare(getPlayerFullName(b.players || {}), undefined, {
          sensitivity: 'base',
        })
      );
  }, [members, memberSearch, memberStatusFilter, memberRoleFilter]);

  const membersTotalPages = Math.max(1, Math.ceil(filteredMembers.length / membersItemsPerPage));

  const paginatedMembers = useMemo(() => {
    const start = (membersCurrentPage - 1) * membersItemsPerPage;
    return filteredMembers.slice(start, start + membersItemsPerPage);
  }, [filteredMembers, membersCurrentPage, membersItemsPerPage]);

  useEffect(() => {
    setMembersCurrentPage(1);
  }, [memberSearch, memberStatusFilter, memberRoleFilter]);

  useEffect(() => {
    if (membersCurrentPage > membersTotalPages) {
      setMembersCurrentPage(membersTotalPages);
    }
  }, [membersCurrentPage, membersTotalPages]);

  const handleMembersPageChange = (page, perPage) => {
    setMembersCurrentPage(page);
    if (perPage != null) {
      setMembersItemsPerPage(perPage);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <StatusBadge variant="warning" size="small">Pending</StatusBadge>;
      case 'approved':
        return <StatusBadge variant="success" size="small">Approved</StatusBadge>;
      case 'rejected':
        return <StatusBadge variant="danger" size="small">Rejected</StatusBadge>;
      default:
        return <StatusBadge variant="default" size="small">{status}</StatusBadge>;
    }
  };

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  useEffect(() => {
    loadReactivationRequests();
  }, [loadReactivationRequests]);

  const memberDisplayName = (member) => {
    const player = member?.players || {};
    return player.first_name || player.last_name
      ? `${player.first_name || ''} ${player.last_name || ''}`.trim()
      : 'Unknown Player';
  };

  const handleTrialToggle = async (member, isTrial) => {
    if (!adminClub?.id) return;
    setMembershipActionFor(member.player_id);
    setMembersError(null);
    try {
      await setClubMemberTrial(adminClub.id, member.player_id, isTrial);
      setMembers((prev) =>
        prev.map((row) =>
          row.player_id === member.player_id ? { ...row, is_trial: isTrial } : row
        )
      );
    } catch (err) {
      console.error('[ClubAdmin] Failed to update trial label:', err);
      setMembersError(err.message || 'Failed to update trial label');
    } finally {
      setMembershipActionFor(null);
    }
  };

  const handleConfirmInactive = async () => {
    const member = inactiveConfirmMember;
    if (!adminClub?.id || !member) return;
    setMembershipActionFor(member.player_id);
    setMembersError(null);
    try {
      await setClubMemberInactive(adminClub.id, member.player_id);
      setMembers((prev) =>
        prev.map((row) =>
          row.player_id === member.player_id
            ? {
                ...row,
                membership_status: 'inactive',
                is_active: false,
                club_role: 'member',
                reactivation_requested_at: null,
              }
            : row
        )
      );
      setInactiveConfirmMember(null);
      await loadReactivationRequests();
    } catch (err) {
      console.error('[ClubAdmin] Failed to set member inactive:', err);
      setMembersError(err.message || 'Failed to set member inactive');
    } finally {
      setMembershipActionFor(null);
    }
  };

  const handleRestoreActive = async (member) => {
    if (!adminClub?.id) return;
    setMembershipActionFor(member.player_id);
    setMembersError(null);
    try {
      await setClubMemberActive(adminClub.id, member.player_id);
      setMembers((prev) =>
        prev.map((row) =>
          row.player_id === member.player_id
            ? {
                ...row,
                membership_status: 'active',
                is_active: true,
                reactivation_requested_at: null,
              }
            : row
        )
      );
      await loadReactivationRequests();
    } catch (err) {
      console.error('[ClubAdmin] Failed to restore member:', err);
      setMembersError(err.message || 'Failed to restore member');
    } finally {
      setMembershipActionFor(null);
    }
  };

  const handleReactivationDecision = async (playerId, approve) => {
    if (!adminClub?.id) return;
    setMembershipActionFor(playerId);
    setMembersError(null);
    try {
      if (approve) {
        await approveClubReactivation(adminClub.id, playerId);
      } else {
        await declineClubReactivation(adminClub.id, playerId);
      }
      await Promise.all([fetchMembers(), loadReactivationRequests()]);
    } catch (err) {
      console.error('[ClubAdmin] Failed to handle reactivation:', err);
      setMembersError(err.message || 'Failed to update reactivation request');
    } finally {
      setMembershipActionFor(null);
    }
  };

  const handleRoleChange = async (playerId, newRole) => {
    if (!adminClub?.id || !newRole) return;

    if (wouldRemoveLastClubAdmin(members, playerId, newRole)) {
      setMembersError(
        'This club must keep at least one admin. Promote another member to admin before changing this role.'
      );
      return;
    }

    setUpdatingRoleFor(playerId);
    setMembersError(null);

    try {
      const { error } = await supabase
        .from('players_club')
        .update({ club_role: newRole })
        .eq('club_id', adminClub.id)
        .eq('player_id', playerId);

      if (error) throw error;

      // Update local state
      setMembers(prev =>
        prev.map(m =>
          m.player_id === playerId ? { ...m, club_role: newRole } : m
        )
      );
    } catch (err) {
      console.error('[ClubAdmin] Failed to update member role:', err);
      setMembersError('Failed to update member role. Please try again.');
    } finally {
      setUpdatingRoleFor(null);
    }
  };

  return (
    <div className="space-y-8">
      <div className="rounded-xl border border-border-medium bg-surface p-4 shadow-sm sm:p-6">
        <h2 className="mb-4 text-xl font-bold text-text-primary sm:mb-6 sm:text-2xl">Join Requests</h2>
        
        {requestsLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <span className="ml-2 text-text-secondary">Loading requests...</span>
          </div>
        ) : showEmptyState ? (
          <div className="text-center py-8">
            <div className="text-4xl mb-4">📭</div>
            <h3 className="text-lg font-semibold text-text-primary mb-2">No Join Requests</h3>
            <p className="text-text-secondary">
              No players have requested to join or link their account yet.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {hasPendingAccountLinks && (
              <div>
                <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
                  <StatusBadge variant="info" size="small" className="mr-2">
                    {accountLinkRequests.length}
                  </StatusBadge>
                  Account link requests
                </h3>
                <div className="space-y-4">
                  {accountLinkRequests.map((request) => (
                    <AccountLinkRequestCard
                      key={request.user_id}
                      request={request}
                      clubId={adminClub.id}
                      currentUser={currentUser}
                      onResolved={handleAccountLinkResolved}
                    />
                  ))}
                </div>
              </div>
            )}

            {reactivationRequests.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
                  <StatusBadge variant="warning" size="small" className="mr-2">
                    {reactivationRequests.length}
                  </StatusBadge>
                  Reactivation requests
                </h3>
                <div className="space-y-4">
                  {reactivationRequests.map((request) => (
                    <div key={request.player_id} className="rounded-lg border border-border-medium bg-surface-subtle p-4">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="mb-2 flex flex-wrap items-center gap-2">
                            <h4 className="font-semibold text-text-primary">{request.player_name || 'Player'}</h4>
                            {request.is_trial ? (
                              <StatusBadge variant="warning" size="small">On trial</StatusBadge>
                            ) : null}
                          </div>
                          <p className="mb-2 text-sm text-text-secondary">{request.player_email || '—'}</p>
                          <p className="text-xs text-text-muted">
                            Requested on {request.reactivation_requested_at
                              ? new Date(request.reactivation_requested_at).toLocaleDateString()
                              : '—'}
                          </p>
                        </div>
                        <div className="flex shrink-0 gap-2 sm:ml-4">
                          <Button
                            variant="success"
                            size="small"
                            onClick={() => handleReactivationDecision(request.player_id, true)}
                            disabled={membershipActionFor === request.player_id}
                          >
                            Approve
                          </Button>
                          <Button
                            variant="danger"
                            size="small"
                            onClick={() => handleReactivationDecision(request.player_id, false)}
                            disabled={membershipActionFor === request.player_id}
                          >
                            Decline
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {hasPendingJoinRequests && (
              <div>
                <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
                  <StatusBadge variant="warning" size="small" className="mr-2">
                    {pendingRequests.length}
                  </StatusBadge>
                  Pending join requests
                </h3>
                <div className="space-y-4">
                  {pendingRequests.map((request) => (
                    <div key={request.id} className="rounded-lg border border-border-medium bg-surface-subtle p-4">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div className="flex min-w-0 flex-1 gap-3">
                          <PlayerAvatar player={joinRequestAvatarPlayer(request)} size="lg" />
                          <div className="min-w-0 flex-1">
                          <div className="mb-2 flex flex-wrap items-center gap-2">
                            <h4 className="font-semibold text-text-primary">{request.player_name}</h4>
                            {getStatusBadge(request.status)}
                          </div>
                          <p className="mb-2 text-sm text-text-secondary">{request.player_email}</p>
                          {request.message && (
                            <div className="mb-3 rounded border border-border bg-surface p-3">
                              <p className="text-sm italic text-text-secondary">"{request.message}"</p>
                            </div>
                          )}
                          <p className="text-xs text-text-muted">
                            Requested on {new Date(request.created_at).toLocaleDateString()}
                          </p>
                          </div>
                        </div>
                        <div className="flex shrink-0 gap-2 sm:ml-4">
                          <button
                            type="button"
                            onClick={() => handleJoinRequest(request.id, 'approved')}
                            disabled={loading}
                            className="rounded-lg bg-success px-4 py-2 text-sm font-medium text-text-inverse hover:bg-success-dark disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {loading ? 'Approving...' : 'Approve'}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRejectClick(request)}
                            disabled={loading}
                            className="rounded-lg bg-danger px-4 py-2 text-sm font-medium text-text-inverse hover:bg-danger-dark disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            Reject
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Request History */}
            {joinRequests.length > 0 && (
              <div>
                <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <h3 className="text-lg font-semibold text-text-primary">Request History</h3>
                  <select
                    value={historyFilter}
                    onChange={(e) => setHistoryFilter(e.target.value)}
                    className="form-input w-full rounded-lg border border-border bg-surface px-3 py-1.5 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary sm:w-auto"
                  >
                    <option value="pending">Pending</option>
                    <option value="all">All Status</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="withdrawn_by_user">Withdrawn</option>
                  </select>
                </div>
                {filteredRequests.length === 0 ? (
                  <div className="text-center py-6 text-text-muted text-sm">
                    No {historyFilter !== 'all' ? historyFilter.replace('_', ' ') : ''} requests found.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredRequests
                      .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
                      .map((request) => {
                        const canApproveRejected = request.status === 'rejected' && !request.withdrawn_at;
                        return (
                          <div key={request.id} className="flex items-center justify-between py-3 px-4 bg-surface-subtle rounded-lg border border-border-medium">
                            <div className="flex items-center space-x-3 flex-1 min-w-0">
                              <PlayerAvatar player={joinRequestAvatarPlayer(request)} size="md" />
                              <span className="font-medium text-text-primary truncate">{request.player_name}</span>
                              {getStatusBadge(request.status)}
                              {request.rejection_message && request.status === 'rejected' && (
                                <span className="text-xs text-text-muted italic">
                                  "{request.rejection_message}"
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-3">
                              {canApproveRejected && (
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-text-secondary">Change to approve:</span>
                                  <button
                                    onClick={() => handleJoinRequest(request.id, 'approved')}
                                    disabled={loading}
                                    className="px-3 py-1.5 bg-success hover:bg-success-dark text-white rounded text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                  >
                                    Approve
                                  </button>
                                </div>
                              )}
                              <span className="text-sm text-text-muted whitespace-nowrap">
                                {new Date(request.updated_at).toLocaleDateString('en-GB', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric'
                                })}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="rounded-xl border border-border-medium bg-surface p-4 shadow-sm sm:p-6">
        <h2 className="mb-2 text-xl font-bold text-text-primary sm:text-2xl">Club Members</h2>
        <p className="text-sm text-text-secondary mb-6">
          Owners and admins can update roles, mark players on trial, set inactive, and keep private notes.
        </p>

        {membersError && (
          <div className="mb-4 text-sm text-danger-dark">
            {membersError}
          </div>
        )}

        {!membersLoading && members.length > 0 && (
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="flex-1">
              <MobileSearchField
                value={memberSearch}
                onChange={(e) => setMemberSearch(e.target.value)}
                placeholder="Search by name or email…"
                showIcon
                aria-label="Search club members"
              />
            </div>
            <select
              value={memberStatusFilter}
              onChange={(e) => setMemberStatusFilter(e.target.value)}
              className="form-input w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm sm:w-40"
              aria-label="Filter members by status"
            >
              <option value="active">Active</option>
              <option value="trial">On trial</option>
              <option value="inactive">Inactive</option>
              <option value="all">All statuses</option>
            </select>
            <select
              value={memberRoleFilter}
              onChange={(e) => setMemberRoleFilter(e.target.value)}
              className="form-input w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm sm:w-40"
              aria-label="Filter members by role"
            >
              <option value="all">All roles</option>
              <option value="member">Member</option>
              <option value="host">Host</option>
              <option value="admin">Admin</option>
              <option value="owner">Owner</option>
            </select>
          </div>
        )}

        {membersLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <span className="ml-2 text-text-secondary">Loading members...</span>
          </div>
        ) : members.length === 0 ? (
        <div className="text-center py-8">
          <div className="text-4xl mb-4">👥</div>
            <h3 className="text-lg font-semibold text-text-primary mb-2">No Members Yet</h3>
            <p className="text-text-secondary">
              When players join your club, they will appear here for role management.
            </p>
        </div>
        ) : filteredMembers.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-4xl mb-4">🔍</div>
            <h3 className="text-lg font-semibold text-text-primary mb-2">No Matching Members</h3>
            <p className="text-text-secondary">
              {memberSearch.trim()
                ? `No members match "${memberSearch.trim()}".`
                : 'No members match the selected filters.'}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-border-medium">
                <thead>
                  <tr className="bg-surface-subtle">
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-text-secondary">
                      Member
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-text-secondary">
                      Role
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-text-secondary">
                      Trial
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-medium uppercase tracking-wider text-text-secondary">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {paginatedMembers.map((member) => (
                    <ClubMemberRow
                      key={member.player_id}
                      member={member}
                      loading={loading}
                      updatingRoleFor={updatingRoleFor}
                      membershipActionFor={membershipActionFor}
                      onRoleChange={handleRoleChange}
                      onTrialToggle={handleTrialToggle}
                      onNotes={setNotesMember}
                      onRestore={handleRestoreActive}
                      onSetInactive={setInactiveConfirmMember}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {membersTotalPages > 1 && (
              <div className="mt-4 border-t border-border-medium pt-2">
                <Pagination
                  currentPage={membersCurrentPage}
                  totalPages={membersTotalPages}
                  totalItems={filteredMembers.length}
                  itemsPerPage={membersItemsPerPage}
                  onPageChange={handleMembersPageChange}
                  showItemsPerPage
                  itemsPerPageOptions={[10, 25, 50]}
                />
              </div>
            )}
          </>
        )}
      </div>

      <ClubMemberNotesModal
        isOpen={Boolean(notesMember)}
        onClose={() => setNotesMember(null)}
        clubId={adminClub?.id}
        playerId={notesMember?.player_id}
        playerName={notesMember ? memberDisplayName(notesMember) : ''}
      />
      <ConfirmationModal
        isOpen={Boolean(inactiveConfirmMember)}
        onClose={() => setInactiveConfirmMember(null)}
        onConfirm={handleConfirmInactive}
        title="Set member inactive"
        isDangerous
        confirmText="Set inactive"
        message={
          <div className="space-y-2">
            <p>
              {inactiveConfirmMember
                ? `${memberDisplayName(inactiveConfirmMember)} will lose signup and club activity access.`
                : 'This member will lose signup and club activity access.'}
            </p>
            <p className="text-sm text-text-secondary">
              Their games, rating, and notes stay on the record. They can request to reactivate later.
            </p>
          </div>
        }
        loading={membershipActionFor === inactiveConfirmMember?.player_id}
      />
    </div>
  );
};

// Inner component that uses season context
const ClubAdminContent = ({ currentUser, clubId, selectedClub, userClubRole }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { season, loading: seasonLoading, setSeason } = useSeason();
  // Read activeTab from location state if provided, otherwise default to 'player-management'
  const initialTab = location.state?.activeTab || 'player-management';
  const [activeTab, setActiveTab] = useState(initialTab); // 'player-management', 'guest-management', 'league-squad', 'paste-aliases'
  
  // Update activeTab when location state changes
  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    }
  }, [location.state?.activeTab]);
  
  const [clubStrength, setClubStrength] = useState(null);
  const [clubStrengthLoading, setClubStrengthLoading] = useState(false);
  const [clubStrengthError, setClubStrengthError] = useState(null);
  const [showEndSessionModal, setShowEndSessionModal] = useState(false);
  const [endingSession, setEndingSession] = useState(false);
  const [endSessionError, setEndSessionError] = useState(null);
  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const [seasonOpLoading, setSeasonOpLoading] = useState(false);
  const [seasonOpError, setSeasonOpError] = useState(null);
  const [seasonOpSuccess, setSeasonOpSuccess] = useState(null);
  
  
  // Season creation confirmation modal
  const [showSeasonConfirmModal, setShowSeasonConfirmModal] = useState(false);
  const [showManageCourts, setShowManageCourts] = useState(false);
  const [showManagePlayers, setShowManagePlayers] = useState(false);
  const [partnerships, setPartnerships] = useState([]); // Add partnerships state
  const [autoEndSessionEnabled, setAutoEndSessionEnabled] = useState(true); // Track if auto-end is enabled
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [seasonName, setSeasonName] = useState('');
  const [joinRequests, setJoinRequests] = useState([]);
  const [joinRequestsLoading, setJoinRequestsLoading] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedRequestForReject, setSelectedRequestForReject] = useState(null);
  
  // Analytics state
  const [analytics, setAnalytics] = useState({
    totalPlayers: 0,
    activePlayers: 0,
    totalSessions: 0,
    sessionsThisMonth: 0,
    averageSessionSize: 0,
    genderSplit: { male: 0, female: 0, other: 0 },
    topPlayers: [],
    recentSessions: [],
    activePlayers30Days: 0,
    activePlayers30DaysPercentage: 0,
    averageElo: { mixed: 0, levels: 0 }
  });

  // State for available clubs
  const [availableClubs, setAvailableClubs] = useState([]);
  
  // Fetch available clubs for admin
  useEffect(() => {
    const fetchAvailableClubs = async () => {
      if (!currentUser?.id) return;
      
      try {
        // Check if user is platform admin or club owner
        if (currentUser.role === 'admin' || currentUser.role === 'super_admin') {
          // For platform admins, they can access all clubs
          const { data: clubs, error } = await supabase
            .from('clubs')
            .select('*')
            .order('name');
            
          if (error) throw error;
          setAvailableClubs(clubs || []);
        } else {
          // For regular users, get clubs where they have admin role
          const { data: adminClubs, error } = await supabase
            .from('players_club')
            .select(`
              club_role,
              clubs (*)
            `)
            .eq('player_id', currentUser.id)
            .eq('club_role', 'admin');
          
          if (error) throw error;
          
          if (adminClubs && adminClubs.length > 0) {
            const clubs = adminClubs.map(ac => ac.clubs);
            setAvailableClubs(clubs);
          } else {
            setError('No admin access found');
          }
        }
      } catch (error) {
        console.error('Error fetching available clubs:', error);
        setError('Failed to load clubs');
      }
    };

    fetchAvailableClubs();
  }, [currentUser?.id]);

  // Fetch analytics data
  useEffect(() => {
    if (!selectedClub?.id) return;
    
    const fetchAnalytics = async () => {
      try {
        console.log('Fetching analytics for club:', selectedClub.id);
        
        // Get total players
        const { data: players, error: playersError } = await supabase
          .from('players_club')
          .select(`
            player_id,
            players!inner(id, gender)
          `)
          .eq('club_id', selectedClub.id);
        
        if (playersError) throw playersError;

        // Get active players from players_club table
        const { data: activePlayersData, error: activePlayersError } = await supabase
          .from('players_club')
          .select('player_id, is_active')
          .eq('club_id', selectedClub.id)
          .eq('is_active', true);
        
        if (activePlayersError) throw activePlayersError;

        // Get sessions
        const { data: sessions, error: sessionsError } = await supabase
          .from('sessions')
          .select('id, session_date, session_status')
          .eq('club_id', selectedClub.id)
          .order('session_date', { ascending: false })
          .limit(10);

        if (sessionsError) throw sessionsError;

        // Get session players for average session size
        const { data: sessionPlayers, error: sessionPlayersError } = await supabase
          .from('session_players')
          .select('session_id')
          .in('session_id', sessions.map(s => s.id));

        if (sessionPlayersError) throw sessionPlayersError;

        // Get ELO ratings
        let eloRatings = [];
        if (season?.id) {
          const { data: eloData, error: eloError } = await supabase
            .from('player_club_elo_ratings')
            .select('mixed_elo_rating, levels_elo_rating')
            .eq('club_id', selectedClub.id)
            .eq('season_id', season.id);

          if (eloError) throw eloError;
          eloRatings = eloData || [];
        } else {
          console.log('No season available, skipping ELO ratings query');
        }

        // Calculate analytics
        const totalPlayers = players.length;
        const activePlayers = activePlayersData.length;
        const totalSessions = sessions.length;
        const sessionsThisMonth = sessions.filter(s => {
          const sessionDate = new Date(s.session_date);
          const now = new Date();
          return sessionDate.getMonth() === now.getMonth() && 
                 sessionDate.getFullYear() === now.getFullYear();
        }).length;

        // Calculate gender split
        const genderSplit = players.reduce((acc, playerClub) => {
          const gender = playerClub.players.gender;
          acc[gender] = (acc[gender] || 0) + 1;
          return acc;
        }, { male: 0, female: 0, other: 0 });

        // Calculate average session size
        const sessionPlayerCounts = {};
        sessionPlayers.forEach(sp => {
          sessionPlayerCounts[sp.session_id] = (sessionPlayerCounts[sp.session_id] || 0) + 1;
        });
        const averageSessionSize = Object.values(sessionPlayerCounts).length > 0 
          ? Math.round(Object.values(sessionPlayerCounts).reduce((a, b) => a + b, 0) / Object.values(sessionPlayerCounts).length)
          : 0;

        // Calculate average ELO
        const avgMixedElo = eloRatings.length > 0 
          ? Math.round(eloRatings.reduce((sum, r) => sum + r.mixed_elo_rating, 0) / eloRatings.length)
          : 1200;
        const avgLevelsElo = eloRatings.length > 0 
          ? Math.round(eloRatings.reduce((sum, r) => sum + r.levels_elo_rating, 0) / eloRatings.length)
          : 1200;

        // Calculate active players in last 30 days
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        
        const { data: recentSessions, error: recentSessionsError } = await supabase
          .from('sessions')
          .select('id')
          .eq('club_id', selectedClub.id)
          .gte('session_date', thirtyDaysAgo.toISOString().split('T')[0]);

        if (recentSessionsError) throw recentSessionsError;

        let activePlayers30Days = 0;
        let activePlayers30DaysPercentage = 0;

        if (recentSessions && recentSessions.length > 0) {
          const recentSessionIds = recentSessions.map(s => s.id);
          const { data: recentPlayers, error: recentPlayersError } = await supabase
            .from('session_players')
            .select('player_id')
            .in('session_id', recentSessionIds);

          if (recentPlayersError) throw recentPlayersError;

          activePlayers30Days = new Set(recentPlayers.map(p => p.player_id)).size;
          activePlayers30DaysPercentage = totalPlayers > 0 ? Math.round((activePlayers30Days / totalPlayers) * 100) : 0;
        }

        console.log('Analytics calculated:', {
          totalPlayers,
          activePlayers,
          totalSessions,
          sessionsThisMonth,
          averageSessionSize,
          genderSplit,
          activePlayers30Days,
          activePlayers30DaysPercentage,
          averageElo: { mixed: avgMixedElo, levels: avgLevelsElo }
        });

        setAnalytics({
          totalPlayers,
          activePlayers,
          totalSessions,
          sessionsThisMonth,
          averageSessionSize,
          genderSplit,
          topPlayers: [], // TODO: Implement top players by ELO
          recentSessions: sessions.slice(0, 5),
          activePlayers30Days,
          activePlayers30DaysPercentage,
          averageElo: { mixed: avgMixedElo, levels: avgLevelsElo }
        });

      } catch (error) {
        console.error('Error fetching analytics:', error);
        setError('Failed to load analytics data');
      }
    };

    fetchAnalytics();
  }, [selectedClub?.id, season?.id]);


  // Fetch join requests when admin club changes
  useEffect(() => {
    const fetchJoinRequests = async () => {
      if (!selectedClub?.id) {
        console.log('No selectedClub.id, skipping join requests fetch');
        return;
      }
      
      console.log('Fetching join requests for club:', selectedClub.id, selectedClub.name);
      
      try {
        setJoinRequestsLoading(true);
        
        // Use the function we created in the database
        const { data, error } = await supabase
          .rpc('get_club_join_requests', {
            club_id_param: selectedClub.id,
            status_filter: 'all'
          });

        console.log('Join requests response:', { data, error });

        if (error) throw error;
        
        console.log('Setting join requests:', data || []);
        setJoinRequests(data || []);
      } catch (error) {
        console.error('Error fetching join requests:', error);
        setError('Failed to load join requests');
      } finally {
        setJoinRequestsLoading(false);
      }
    };

    fetchJoinRequests();
  }, [selectedClub?.id]);

  const handleJoinRequest = async (requestId, action, rejectionData = null) => {
    try {
      setLoading(true);
      setError(null);
      
      if (!currentUser?.id) {
        throw new Error('User not authenticated');
      }

      let result;
      
      if (action === 'approved') {
        // Use the approve_join_request RPC function
        const { data, error: rpcError } = await supabase
          .rpc('approve_join_request', {
            p_request_id: requestId,
            p_approved_by: currentUser.id
          });

        if (rpcError) throw rpcError;
        result = data;
        
        // Check if RPC returned an error
        if (result?.status === 'error') {
          throw new Error(result.error || 'Failed to approve join request');
        }
      } else if (action === 'rejected') {
        // Use the reject_join_request RPC function with provided data
        // Note: cooldownDays can be 0, so we need to check for undefined/null, not falsy
        const cooldownDays = rejectionData?.cooldownDays !== undefined && rejectionData?.cooldownDays !== null 
          ? rejectionData.cooldownDays 
          : 7;
        const { data, error: rpcError } = await supabase
          .rpc('reject_join_request', {
            p_request_id: requestId,
            p_rejected_by: currentUser.id,
            p_rejection_message: rejectionData?.rejectionMessage || null,
            p_cooldown_days: cooldownDays,
            p_block_future: rejectionData?.blockFuture || false
          });

        if (rpcError) throw rpcError;
        result = data;
        
        // Check if RPC returned an error
        if (result?.status === 'error') {
          throw new Error(result.error || 'Failed to reject join request');
        }
      } else {
        throw new Error(`Unknown action: ${action}`);
      }

      setSuccess(`Join request ${action === 'approved' ? 'approved' : 'rejected'} successfully!`);
      
      // Close reject modal if open
      if (showRejectModal) {
        setShowRejectModal(false);
        setSelectedRequestForReject(null);
      }
      
      // Refresh join requests
      const { data, error } = await supabase
        .rpc('get_club_join_requests', {
          club_id_param: selectedClub.id,
          status_filter: 'all'
        });

      if (error) throw error;
      setJoinRequests(data || []);
      
    } catch (error) {
      console.error(`Error ${action}ing join request:`, error);
      setError(error.message || `Failed to ${action} join request`);
    } finally {
      setLoading(false);
    }
  };

  const handleRejectClick = (request) => {
    setSelectedRequestForReject(request);
    setShowRejectModal(true);
  };

  const handleRejectConfirm = (rejectionData) => {
    if (selectedRequestForReject) {
      handleJoinRequest(selectedRequestForReject.id, 'rejected', rejectionData);
    }
  };

  const handleStartNewSeason = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    setShowSeasonConfirmModal(false);
    
    try {
      // 1. Mark ALL current seasons as 'completed' (handle multiple current seasons)
      console.log('Marking all current seasons as completed for club:', selectedClub.id);
      const { error: updateCurrentError } = await supabase
        .from('seasons')
        .update({ 
          season_status: 'completed', 
          end_date: new Date().toISOString() 
        })
        .eq('club_id', selectedClub.id)
        .eq('season_status', 'current');
        
      if (updateCurrentError) {
        console.error('Error updating current seasons:', updateCurrentError);
        throw updateCurrentError;
      }
      
      // 2. Get the highest season number to determine the next one
      const { data: latestSeason, error: latestError } = await supabase
        .from('seasons')
        .select('season_number')
        .eq('club_id', selectedClub.id)
        .order('season_number', { ascending: false })
        .limit(1)
        .maybeSingle();
        
      if (latestError) throw latestError;
      
      const nextSeasonNumber = (latestSeason?.season_number || 0) + 1;
      
      // 3. Insert new season
      const nameToUse = seasonName.trim() || `Season ${nextSeasonNumber}`;
      const { data: newSeason, error: newSeasonError } = await supabase
        .from('seasons')
        .insert([{
          club_id: selectedClub.id,
          season_number: nextSeasonNumber,
          name: nameToUse,
          created_by: currentUser.id,
          season_status: 'current',
          start_date: new Date().toISOString()
        }])
        .select()
        .single();
        
      if (newSeasonError) throw newSeasonError;
      
      // 4. Get all active players in club
      const { data: players, error: playersError } = await supabase
        .from('players_club')
        .select('player_id')
        .eq('club_id', selectedClub.id)
        .eq('is_active', true);
        
      if (playersError) throw playersError;
      
      // 5. Insert new player_club_elo_ratings rows for each player
      const baseElo = getDefaultStartingElo(selectedClub?.strength_rating);
      const newRatings = players.map(p => ({
        player_id: p.player_id,
        club_id: selectedClub.id,
        season_id: newSeason.id,
        mixed_elo_rating: baseElo,
        levels_elo_rating: baseElo,
        singles_elo_rating: 1000,
        mixed_games_played: 0,
        levels_games_played: 0,
        singles_games_played: 0,
        unbalanced_games_played: 0
      }));
      
      if (newRatings.length > 0) {
        const { error: ratingsError } = await supabase
          .from('player_club_elo_ratings')
          .insert(newRatings);
        if (ratingsError) throw ratingsError;
      }
      
      setSeason(newSeason);
      setSuccess(`New season "${nameToUse}" started successfully!`);
      setSeasonName(''); // Clear input on success
    } catch (err) {
      setError(err.message || 'Failed to start new season');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={APP_PAGE_BLEED_ROOT}>
      <div className={`mx-auto max-w-app-shell ${APP_PAGE_GUTTER} py-6 sm:py-8`}>
        <div className="mb-16">
          <BackButton
            hideOnDesktop
            onClick={() => {
              navigateBack(navigate, location, clubId, `/club-management/${clubId}`);
            }}
          >
            Back to Management
          </BackButton>

          <div className="text-center">
            <Heading level={1} className="text-display text-text-primary mb-6">Player Management</Heading>
            <Text className="text-body-large text-text-secondary max-w-2xl mx-auto">
              Managing: <span className="font-semibold text-text-primary">{selectedClub?.name || 'Unknown Club'}</span> - Manage players, guests, join requests, and league links
            </Text>
            {error && (
              <Card variant="danger" size="medium" className="mt-8">
                <Typography variant="body" className="text-danger">{error}</Typography>
              </Card>
            )}
          </div>
        </div>

      <div className="mb-8 border-b border-border-medium">
        <nav className="-mb-px flex flex-wrap gap-x-4 gap-y-2 sm:gap-x-8">
          <button
            type="button"
            onClick={() => setActiveTab('player-management')}
            className={`border-b-2 px-1 py-2 text-sm font-medium ${
              activeTab === 'player-management'
                ? 'border-primary text-text-primary'
                : 'border-transparent text-text-muted hover:border-border hover:text-text-secondary'
            }`}
          >
            👥 Player Management
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('guest-management')}
            className={`border-b-2 px-1 py-2 text-sm font-medium ${
              activeTab === 'guest-management'
                ? 'border-primary text-text-primary'
                : 'border-transparent text-text-muted hover:border-border hover:text-text-secondary'
            }`}
          >
            👤 Guest Management
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('paste-aliases')}
            className={`border-b-2 px-1 py-2 text-sm font-medium ${
              activeTab === 'paste-aliases'
                ? 'border-primary text-text-primary'
                : 'border-transparent text-text-muted hover:border-border hover:text-text-secondary'
            }`}
          >
            📋 Spreadsheet names
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('league-squad')}
            className={`border-b-2 px-1 py-2 text-sm font-medium ${
              activeTab === 'league-squad'
                ? 'border-primary text-text-primary'
                : 'border-transparent text-text-muted hover:border-border hover:text-text-secondary'
            }`}
          >
            🏟️ League squad
          </button>
        </nav>
      </div>

      {/* Tab Content */}



        {activeTab === 'player-management' && (
          <PlayerManagementContent 
            adminClub={selectedClub}
            joinRequests={joinRequests}
            joinRequestsLoading={joinRequestsLoading}
            handleJoinRequest={handleJoinRequest}
            handleRejectClick={handleRejectClick}
            loading={loading}
            currentUser={currentUser}
            onJoinRequestsChanged={() => {
              if (!selectedClub?.id) return;
              supabase
                .rpc('get_club_join_requests', {
                  club_id_param: selectedClub.id,
                  status_filter: 'all',
                })
                .then(({ data }) => setJoinRequests(data || []))
                .catch((err) => console.error('Error refreshing join requests:', err));
            }}
          />
        )}

        {activeTab === 'guest-management' && (
          <GuestManagement 
            clubId={selectedClub?.id}
            currentUser={currentUser}
            seasonId={season?.id}
          />
        )}

        {activeTab === 'paste-aliases' && selectedClub?.id && (
          <div className="rounded-xl border border-border-medium bg-surface p-4 shadow-sm sm:p-6">
            <PasteAliasManager clubId={selectedClub.id} />
          </div>
        )}

        {activeTab === 'league-squad' && selectedClub?.id && (
          <ClubLeagueSquadSection clubId={selectedClub.id} />
        )}

      {/* Season Creation Confirmation Modal */}
      <ConfirmationModal
        isOpen={showSeasonConfirmModal}
        onClose={() => setShowSeasonConfirmModal(false)}
        onConfirm={handleStartNewSeason}
        title="Start New Season"
        message={
          <div className="space-y-3">
            <p className="text-text-primary">
              Are you sure you want to start a new season? This will:
            </p>
            <ul className="list-disc list-inside text-sm text-text-secondary space-y-1 ml-4">
              <li>Mark all current seasons as <strong>completed</strong></li>
              <li>Create a new season with status <strong>current</strong></li>
              <li>Reset all player ELO ratings to the club default ({getDefaultStartingElo(selectedClub?.strength_rating)}) for the new season</li>
              <li>Preserve all historical data from previous seasons</li>
            </ul>
            <p className="text-sm text-text-secondary mt-3">
              <strong>Season Name:</strong> {seasonName.trim() || `Season ${(season?.season_number || 0) + 1}`}
            </p>
          </div>
        }
        confirmText="Start New Season"
        cancelText="Cancel"
        loading={loading}
      />

      {/* Reject Join Request Modal */}
      <RejectJoinRequestModal
        isOpen={showRejectModal}
        onClose={() => {
          setShowRejectModal(false);
          setSelectedRequestForReject(null);
        }}
        onConfirm={handleRejectConfirm}
        playerName={selectedRequestForReject?.player_name || 'Player'}
        loading={loading}
      />
      </div>
    </div>
  );
};

// Outer component that handles permissions and wraps with SeasonProvider
const ClubAdmin = ({ currentUser }) => {
  const { clubId } = useParams();
  const navigate = useNavigate();
  
  // Use club permissions hook to get club context from URL
  const { 
    club: selectedClub, 
    role: userClubRole, 
    loading: clubLoading, 
    hasPermission, 
    error: clubError 
  } = useClubPermissions(clubId, currentUser);

  const isAdmin =
    currentUser?.role === 'admin' ||
    currentUser?.role === 'super_admin' ||
    userClubRole === 'admin' ||
    userClubRole === 'owner';

  // Show loading state while fetching club permissions
  if (clubLoading) {
    return (
      <SeasonProvider clubId={clubId}>
        <div className="max-w-app-shell mx-auto px-6 py-8">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <Typography variant="muted" className="ml-3">Loading club details...</Typography>
          </div>
        </div>
      </SeasonProvider>
    );
  }

  // Show error if club not found or no permission
  if (clubError || !hasPermission) {
    return (
      <div className="max-w-app-shell mx-auto px-6 py-8">
        <Card variant="default" size="large" className="text-center py-12">
          <div className="text-6xl mb-4">🚫</div>
          <Heading level={2} className="mb-4">Access Denied</Heading>
          <Text className="mb-8 text-text-secondary">
            {clubError || 'You do not have permission to manage this club'}
          </Text>
          <div className="flex justify-center space-x-4">
            <Button onClick={() => navigate('/')} variant="primary">
              Go to Homepage
            </Button>
            <Button onClick={() => navigate('/my-clubs')} variant="outline">
              View My Clubs
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-8">
        <Card variant="danger" size="large" className="text-center">
          <Heading level={2} className="text-danger mb-4">Access Denied</Heading>
          <Text className="text-danger-dark">You do not have permission to access this page.</Text>
        </Card>
      </div>
    );
  }

  return (
    <SeasonProvider clubId={clubId}>
      <ClubAdminContent 
        currentUser={currentUser}
        clubId={clubId}
        selectedClub={selectedClub}
        userClubRole={userClubRole}
      />
    </SeasonProvider>
  );
};

export default ClubAdmin; 